<script lang="ts">
  import CmsImage from '$components/cms-image.svelte';
  import { intersectionObserver } from '$lib/actions/intersection-observer';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { aboveLg } from '$lib/stores/breakpoints';
  import { cn } from '$lib/utils';
  import type { ParalaxMasonryStoryblok } from '$types/bloks';

  export let block: ParalaxMasonryStoryblok;

  let scroll: number;
  let savedScroll = 0;
  let content: HTMLElement;
  let animate = true;

  $: scrollPage = scroll - content?.offsetTop;
  $: savedScrollPage = savedScroll - content?.offsetTop;

  const groupBiggerThanThreeStyles = [
    'mt-[54px] max-w-[332px] aspect-square hidden lg:block lg:order-1',
    'mt-4 lg:mt-[168px] max-w-full lg:max-w-[332px] aspect-square order-3 lg:order-2 self-center z-10',
    'mt-0 max-w-[183px] lg:max-w-[332px] aspect-square order-1 lg:order-3 lg:ml-4%',
    'mt-4 lg:-mt-[30px] max-w-[374px] lg:max-w-[332px] aspect-[187/83] lg:aspect-[2/1] lg:ml-[3%] xl:ml-[8%] order-2 lg:order-4 self-end lg:self-start',
    '-mt-24 max-w-[284px] aspect-square lg:mr-[30px] xl:mr-[128px] hidden lg:block lg:order-5'
  ];

  const groupEqualToThreeStyles = [
    'mt-4 lg:mt-[48px] lg:ml-11 max-w-[183px] lg:max-w-[360px] aspect-square order-2 lg:order-1',
    'mt-4 lg:mt-[148px] max-w-[680px] lg:max-w-[428px] aspect-square lg:aspect-square order-3 lg:order-2 self-center',
    'mt-0 w-full max-w-[183px] lg:max-w-[332px] aspect-square order-1 lg:order-3 self-end lg:self-start'
  ];

  $: imageLargeGroupParallax = [
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.15 : -savedScrollPage * 0.15}px,0)`,
      mobile: `translate3d(0,0,0);`
    },
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.05 : -savedScrollPage * 0.15}px,0)`,
      mobile: `translate3d(0,0,0);`
    },
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.15 : -savedScrollPage * 0.15}px,0)`,
      mobile: `translate3d(${animate ? 100 + scrollPage * 0.2 : 100 + savedScrollPage * 0.2}px,0,0);`
    },
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.1 : -savedScrollPage * 0.2}px,0)`,
      mobile: `translate3d(${animate ? -scrollPage * 0.05 - 25 : -savedScrollPage * 0.05 - 25}px,0,0);`
    },
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.05 : -savedScrollPage * 0.05}px,0)`,
      mobile: `translate3d(0,0,0);`
    }
  ];
  $: imageSmallGroupParallax = [
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.15 : -savedScrollPage * 0.15}px,0)`,
      mobile: `translate3d(${animate ? 100 + scrollPage * 0.2 : 100 + savedScrollPage * 0.2}px,0,0);`
    },
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.05 : -savedScrollPage * 0.15}px,0)`,
      mobile: `translate3d(0,0,0);`
    },
    {
      desktop: `translate3d(0,${animate ? -scrollPage * 0.15 : -savedScrollPage * 0.15}px,0)`,
      mobile: `translate3d(${animate ? -scrollPage * 0.05 - 25 : -savedScrollPage * 0.05 - 25}px,0,0);`
    }
  ];

  $: imageParallax = block.images.length > 3 ? imageLargeGroupParallax : imageSmallGroupParallax;

  const imageStyles =
    block.images.length > 3 ? groupBiggerThanThreeStyles : groupEqualToThreeStyles;
</script>

<svelte:window bind:scrollY={scroll} />
<section
  use:intersectionObserver={{
    callback: ([e]) => {
      if (e.isIntersecting) {
        animate = true;
      } else {
        animate = false;
      }
    },
    options: { threshold: [0] }
  }}
  bind:this={content}
>
  <div
    class="container isolate mx-auto lg:gap-4 px-4 py-11 lg:px-8 lg:py-16"
    use:storyblokEditable={block}
  >
    <div class="grid grid-cols-12 gap-2 mb-8">
      <div class="col-span-12 col-start-1 lg:col-span-6 lg:col-start-4">
        <p class="text-3xl font-semibold">{block.info[0].title}</p>
        <p class="text-xl text-primary/80 mt-2">{block.info[0].text}</p>
      </div>
    </div>
    <div
      class="flex flex-col lg:flex-row justify-between gap-4 mt-14 items-start flex-wrap"
      style="max-width: calc(100% - 1px)"
    >
      {#each block.images as image, i}
        <CmsImage
          image={image.asset}
          class={cn('rounded-md w-full object-cover', imageStyles[i])}
          style={`flex: ${$aboveLg ? '0 0 30%' : 'unset'}; transform: ${$aboveLg ? imageParallax[i].desktop : imageParallax[i].mobile};`}
        />
      {/each}
    </div>
  </div>
</section>
